/**
 * CJAS — calendar-day arithmetic. The one place instants become days.
 *
 * WHY THIS MODULE EXISTS (added 2026-09-09, the 15th mismatch)
 *
 * Core rule 4 says: never use `toISOString()` for a calendar date, and never
 * subtract a date string from an instant. It was written after two bugs and
 * it has now caught three more, because the arithmetic kept being retyped:
 *
 *   - `dashboard.calendarDaysUntil()` and `timetable-manager.runway()` held
 *     byte-identical copies of the correct version. Two places that must
 *     agree about "the timetable runs out in N days", free to drift apart.
 *   - `offline.shouldRefresh()` held the WRONG version — `new Date(last) - now`
 *     — so the app thought it had 7 days of cached prayer times when it had 6,
 *     which is the difference between refreshing before a trip and going blank
 *     during one.
 *   - `giving.freshness()` and `dashboard.importHealth()` both answered
 *     "how many days ago did the Solidus import run" by dividing elapsed
 *     milliseconds. An import that ran at 23:30 last night reads as
 *     "Oppdatert i dag" at 08:00 this morning. The member's question is
 *     "I gave on Friday, why can't I see it" — a CALENDAR question.
 *
 * The convention, matching every other date function in core: **whole
 * calendar days in the runtime's local zone**. The platform is Oslo; a day
 * is what somebody counts on a wall calendar, not a 86 400 000 ms bucket.
 * Nothing here parses a timezone — core stays dependency-free.
 *
 * The consequence, stated so nobody is surprised by it: a fixture built from
 * a UTC instant near midnight lands on a different local date in a different
 * zone, and the timezone matrix will say so. That is the arithmetic being
 * honest, not a flake. Pin such fixtures mid-day (10:00Z is inside 5 Sept in
 * every zone the matrix runs).
 */

/** Local midnight of whatever instant this is. */
function startOfDay(value) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Local midnight of a value that may be a date-only string.
 *
 * `new Date('2026-12-31')` is parsed as UTC midnight by the spec, which is
 * 31 December in Oslo and **30 December in Los Angeles**. A bare YYYY-MM-DD
 * from the timetable is a calendar date, never an instant, so it is built
 * field-by-field instead.
 */
function startOfCalendarDay(value) {
  if (typeof value === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return startOfDay(value);
}

/** Whole calendar days from `now` forward to `target`. Negative if past. */
function calendarDaysUntil(target, now = new Date()) {
  const a = startOfCalendarDay(target);
  const b = startOfDay(now);
  if (!a || !b) return null;
  // Round, not floor: a DST shift makes one of these days 23 or 25 hours long.
  return Math.round((a - b) / 86400000);
}

/** Whole calendar days from `past` up to `now`. Negative if in the future. */
function calendarDaysSince(past, now = new Date()) {
  const days = calendarDaysUntil(past, now);
  return days === null ? null : -days;
}

module.exports = { startOfDay, startOfCalendarDay, calendarDaysUntil, calendarDaysSince };
